import { serverGenerateWithRetry } from '../../../services/geminiTransport';
import type {
  OriginalEngineAITransport,
  OriginalEngineTransportContext,
} from '../types';
import type { GoldenGenerateParams } from '../golden/goldenTransport';
import { logOriginalCompatTransport } from './transportLogging';

export interface OriginalCompatTransportLog {
  agent: string;
  projectId: string;
  chapterNumber: number;
  logicalCallId: string;
  requestAccepted: boolean;
  responseParsed: boolean;
  transportAttempts: number;
}

function mapAgentToTransportLabel(agent: OriginalEngineTransportContext['agent']): string {
  switch (agent) {
    case 'SCRIBE':
      return 'Scribe';
    case 'INSPECTOR':
      return 'Inspector';
    case 'EDITOR':
      return 'Editor';
    case 'PUBLICATION_EDITOR':
      return 'PublicationEditor';
    case 'ARCHIVIST':
      return 'Archivist';
    case 'ARCHITECT':
      return 'Architect';
    case 'DIRECTOR':
      return 'Rewriter';
    default:
      return agent;
  }
}

export class GeminiTransportAdapter implements OriginalEngineAITransport {
  constructor(
    private readonly defaults?: {
      operation?: string;
      /** Compat mode: cap client transport retries per logical call (default 1). */
      maxClientTransportRetries?: number;
    },
  ) {}

  async generate<TRequest, TResponse>(
    request: TRequest,
    context: OriginalEngineTransportContext,
  ): Promise<TResponse> {
    const params = request as GoldenGenerateParams;
    let transportAttempts = 0;
    let responseParsed = false;

    try {
      transportAttempts += 1;
      const response = await serverGenerateWithRetry(params as Record<string, unknown>, {
        policy: 'original_engine_compat',
        operation: this.defaults?.operation ?? 'manuscript_generation',
        agent: mapAgentToTransportLabel(context.agent),
        projectId: context.projectId,
        logicalCallId: context.logicalCallId,
        runId: context.logicalCallId.split(':')[0],
        maxRetries: this.defaults?.maxClientTransportRetries ?? 1,
      });

      responseParsed = typeof response.text === 'string';
      logOriginalCompatTransport({
        agent: context.agent,
        projectId: context.projectId,
        chapterNumber: context.chapterNumber,
        logicalCallId: context.logicalCallId,
        requestAccepted: true,
        responseParsed,
        transportAttempts,
      });

      return response as TResponse;
    } catch (error) {
      logOriginalCompatTransport({
        agent: context.agent,
        projectId: context.projectId,
        chapterNumber: context.chapterNumber,
        logicalCallId: context.logicalCallId,
        requestAccepted: false,
        responseParsed,
        transportAttempts,
      });
      throw error;
    }
  }
}
